import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateInstance } from '../../features/equipment/equipmentSlice';
import { patchProjectMetadata } from '../../features/layout/layoutSlice';
import {
  DEFAULT_PROCESS_TYPE,
  DEFAULT_TOOL_FOUP_CAPACITY,
  DEFAULT_PROCESS_WAIT_SEC,
  DEFAULT_STOCKER_PROCESS_WAIT_SEC,
  OBJECT_TYPE_LABELS,
} from '../../features/equipment/equipmentConstants';
import {
  resolvePorts,
  equipmentProvenance,
  EQUIPMENT_PROVENANCE_LABEL,
} from '../../utils/equipmentGeometry';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';
import PropertySection from './PropertySection';
import LoadPortsEditor from './LoadPortsEditor';
import ProcessTypeSelect from './ProcessTypeSelect';
import { useProjectProcessTypes } from '../../hooks/useProjectProcessTypes';
import {
  primaryProcessType,
  readStockerProcessTypes,
} from '../../features/equipment/stockerProcessTypes';
import { readProcessFlow } from '../../features/simulation/processFlow';
import {
  PropertyHint,
  PropertyInput,
  PropertyNumber,
  PropertyPanelHeader,
  PropertyRow,
  PROPERTY_CONTROL_CLASS,
  ReadOnlyRow,
} from './PropertyFields';

type Props = {
  instance: EquipmentInstance;
};

/**
 * Digital Twin properties for an equipment-overlay instance (Equipment or Stocker).
 * Shown in the shared Properties panel when an overlay object is selected.
 */
const EquipmentInstanceProperties: React.FC<Props> = ({ instance }) => {
  const dispatch = useAppDispatch();
  const templates = useAppSelector((s) => s.equipment.templates);
  const portEditId = useAppSelector((s) => s.equipment.portEditInstanceId);
  const projectMetadata = useAppSelector((s) => s.layout.projectMetadata);
  const template = instance.templateId ? templates[instance.templateId] : null;
  const ports = resolvePorts(instance, template);
  const isStocker = instance.objectType === 'stocker';
  const canShowPorts = equipmentProvenance(instance) !== 'dxf';
  const editingPorts = portEditId === instance.id;
  const { processTypes, addProcessType } = useProjectProcessTypes([
    instance.equipmentType,
    ...(instance.processTypes ?? []),
  ]);

  const patch = (changes: Partial<EquipmentInstance>) => {
    dispatch(updateInstance({ id: instance.id, changes, skipHistory: true }));
  };

  const stockerProcessTypes = readStockerProcessTypes({
    processTypes: instance.processTypes,
    legacyProcessType: instance.equipmentType,
  });

  const updateProcessWaitSec = (processWaitSec: number) => {
    patch({ processWaitSec });
    if (isStocker) return;

    const processFlow = readProcessFlow(projectMetadata);
    let changed = false;
    const steps = processFlow.steps.map((step) => {
      if (!(instance.id in step.toolProcessTimes)) return step;
      changed = true;
      return {
        ...step,
        toolProcessTimes: {
          ...step.toolProcessTimes,
          [instance.id]: processWaitSec,
        },
      };
    });
    if (changed) {
      dispatch(patchProjectMetadata({ processFlow: { ...processFlow, steps } }));
    }
  };

  return (
    <div className="p-2.5">
      <PropertyPanelHeader
        title={instance.name}
        subtitle={isStocker ? OBJECT_TYPE_LABELS.stocker : instance.equipmentType}
      />
      <div className="space-y-1.5">
        <PropertySection title="General">
          <PropertyInput label="Name" value={instance.name} onChange={(name) => patch({ name })} />
          <ReadOnlyRow
            label="Source"
            value={EQUIPMENT_PROVENANCE_LABEL[equipmentProvenance(instance)]}
          />
          {isStocker ? (
            <>
              <ReadOnlyRow label="Type" value={OBJECT_TYPE_LABELS.stocker} />
              <PropertyRow label="Process Type" wide>
                <ProcessTypeSelect
                  value={primaryProcessType(stockerProcessTypes) ?? ''}
                  options={processTypes}
                  allowNone
                  className={PROPERTY_CONTROL_CLASS}
                  onAddCustom={addProcessType}
                  onChange={(processType) => {
                    patch({
                      processTypes: processType ? [processType] : [],
                      ...(processType ? { equipmentType: processType } : {}),
                    });
                  }}
                />
              </PropertyRow>
            </>
          ) : (
            <PropertyRow label="Equipment Type" wide>
              <ProcessTypeSelect
                value={instance.equipmentType ?? DEFAULT_PROCESS_TYPE}
                options={processTypes}
                className={PROPERTY_CONTROL_CLASS}
                onAddCustom={addProcessType}
                onChange={(equipmentType) => {
                  if (equipmentType) patch({ equipmentType });
                }}
              />
            </PropertyRow>
          )}
          <PropertyInput
            label="Description"
            value={instance.description}
            onChange={(description) => patch({ description })}
            placeholder="Optional"
          />
        </PropertySection>

        {isStocker && (
          <PropertySection title="Process">
            <PropertyNumber
              label="Wait Time"
              unit="s"
              value={instance.processWaitSec ?? DEFAULT_STOCKER_PROCESS_WAIT_SEC}
              min={0}
              step={1}
              onChange={(processWaitSec) => patch({ processWaitSec: Math.max(0, processWaitSec) })}
            />
          </PropertySection>
        )}

        {instance.objectType === 'equipment' && (
          <PropertySection title="Process">
            <PropertyNumber
              label="Process Time"
              unit="s"
              value={instance.processWaitSec ?? DEFAULT_PROCESS_WAIT_SEC}
              min={0}
              step={1}
              onChange={(processWaitSec) => updateProcessWaitSec(Math.max(0, processWaitSec))}
            />
            <PropertyNumber
              label="FOUP Capacity"
              value={instance.foupCapacity ?? DEFAULT_TOOL_FOUP_CAPACITY}
              min={1}
              step={1}
              onChange={(foupCapacity) => patch({ foupCapacity })}
            />
            <PropertyHint>
              Shown on simulation inventory badges as current / capacity (e.g. T1: 0 / 2).
            </PropertyHint>
          </PropertySection>
        )}

        {canShowPorts && (
          <PropertySection title="Ports">
            <LoadPortsEditor
              kind="equipment"
              instance={instance}
              ports={ports}
              editing={editingPorts}
            />
          </PropertySection>
        )}
      </div>
    </div>
  );
};

export default EquipmentInstanceProperties;
